import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

const AuthContext = createContext(null);
const TOKEN_KEY = "veloclin_token";
const AUTH_KEY = "veloclin_auth";
const API = process.env.REACT_APP_BACKEND_URL;

const parseDetail = (d) =>
  Array.isArray(d) ? d.map((e) => e?.msg || JSON.stringify(e)).join(" ")
    : typeof d === "string" ? d : d?.msg || "Ocorreu um erro. Tente novamente.";

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null); // null = carregando, false = deslogado, obj = logado

  const loadStored = () => {
    try {
      const raw = localStorage.getItem(AUTH_KEY);
      setCurrentUser(raw ? JSON.parse(raw) : false);
    } catch {
      setCurrentUser(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((d) => { setCurrentUser(d.user); window.dispatchEvent(new Event("veloclin-auth")); })
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          loadStored();
        });
    } else {
      loadStored();
    }
  }, []);

  const register = useCallback(async (payload) => {
    const r = await fetch(`${API}/api/auth/register`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(parseDetail(d.detail));
    localStorage.setItem(TOKEN_KEY, d.token);
    localStorage.removeItem(AUTH_KEY);
    setCurrentUser(d.user);
    window.dispatchEvent(new Event("veloclin-auth"));
    return d;
  }, []);

  const loginReal = useCallback(async (email, password) => {
    const r = await fetch(`${API}/api/auth/login`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(parseDetail(d.detail));
    localStorage.setItem(TOKEN_KEY, d.token);
    localStorage.removeItem(AUTH_KEY);
    setCurrentUser(d.user);
    window.dispatchEvent(new Event("veloclin-auth"));
    return d.user;
  }, []);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    const r = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return null;
    const d = await r.json();
    setCurrentUser(d.user);
    return d.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(AUTH_KEY);
    setCurrentUser(false);
    window.dispatchEvent(new Event("veloclin-auth"));
  }, []);

  const value = useMemo(
    () => ({
      currentUser,
      role: currentUser && currentUser !== null ? currentUser.role ?? null : null,
      isLoading: currentUser === null,
      isAuthenticated: Boolean(currentUser),
      blocked: Boolean(currentUser && currentUser.blocked),
      plan: currentUser?.plan ?? null,
      subscriptionStatus: currentUser?.subscription_status ?? null,
      daysLeft: currentUser?.days_left ?? null,
      register,
      loginReal,
      refreshMe,
      logout,
    }),
    [currentUser, register, loginReal, refreshMe, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return ctx;
};

export const homeForRole = (role) =>
  role === "medico" ? "/portal" : role === "secretaria" ? "/recepcao" : "/agenda";
